import { useCallback, useEffect, useState } from 'react';
import type { Expense, Receipt } from '../types/expense';
import {
  getExpenseReceipts,
  uploadExpenseReceipt,
  getReceiptDownloadUrl,
  getExpense,
} from '../lib/api/endpoints';
import { receiptFromDto, expenseFromDto } from '../lib/api/mappers';
import { comprimirRecibo } from '../utils/receiptImage';

/**
 * FE-12 — las versiones del recibo de un gasto, su subida y su descarga.
 *
 * **El recibo se versiona, no se reemplaza.** Cada subida crea un objeto nuevo
 * y una fila nueva; la vigente es la última y las anteriores no se pueden
 * borrar. Es lo que permite que el write-once de ADR-009 conviva con "corregir
 * es operación de primera clase": una foto mal tomada se sustituye sin que
 * desaparezca la prueba de que se subió.
 *
 * ⚠️ **La URL de descarga se pide AL ABRIR**, nunca al listar. Es una capacidad
 * temporal sobre un objeto concreto: pedir una por fila sería N+1, y además
 * caducaría mientras el usuario mira la lista. Por eso `urlDeDescarga` es una
 * acción, no un campo.
 */
export function useExpenseReceipts(expenseId: string | null) {
  const [receipts, setReceipts] = useState<Receipt[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);
  const [subiendo, setSubiendo] = useState(false);

  useEffect(() => {
    if (!expenseId) {
      setReceipts([]);
      setError(null);
      return;
    }

    let cancelled = false;

    const load = async () => {
      setIsLoading(true);
      setReceipts([]);
      try {
        const res = await getExpenseReceipts(expenseId);
        if (cancelled) return;
        setReceipts(res.items.map(receiptFromDto));
        setError(null);
      } catch (err) {
        if (!cancelled) {
          setReceipts([]);
          setError(err instanceof Error ? err : new Error('Error desconocido cargando los recibos'));
        }
      } finally {
        if (!cancelled) setIsLoading(false);
      }
    };

    load();
    return () => { cancelled = true; };
  }, [expenseId]);

  /**
   * Sube una versión nueva y devuelve el GASTO recargado.
   *
   * Se recarga el gasto entero, y no solo la lista de recibos, porque el
   * `Expense` trae el recibo VIGENTE embebido: sin ese segundo `GET` la fila
   * del listado seguiría marcada «sin recibo» aunque la subida haya ido bien.
   *
   * Se vuelve a llamar a `comprimirRecibo` aunque el selector ya haya preparado
   * el archivo: la función es **idempotente**, así que no recodifica nada, y
   * llamarla aquí garantiza que ningún camino futuro suba un `File` en crudo
   * por no acordarse. Los dos caminos que suben —el formulario y el detalle—
   * pasan por la misma preparación sin tener que coordinarse.
   */
  const subir = useCallback(async (file: File): Promise<Expense> => {
    if (!expenseId) throw new Error('No hay gasto al que adjuntar el recibo.');
    setSubiendo(true);
    try {
      const listo = await comprimirRecibo(file);
      const recibo = receiptFromDto(await uploadExpenseReceipt(expenseId, listo));
      setReceipts(prev => [recibo, ...prev]);
      return expenseFromDto(await getExpense(expenseId));
    } finally {
      setSubiendo(false);
    }
  }, [expenseId]);

  /** Capacidad temporal sobre un recibo concreto. Sirve igual para la vigente y para las anteriores. */
  const urlDeDescarga = useCallback(async (receiptId: string): Promise<string> => {
    if (!expenseId) throw new Error('No hay gasto del que descargar el recibo.');
    const res = await getReceiptDownloadUrl(expenseId, receiptId);
    return res.url;
  }, [expenseId]);

  return { receipts, isLoading, error, subiendo, subir, urlDeDescarga };
}
